import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2, Send, ArrowLeft, Plus, MessageSquare, Megaphone, Languages, Trash2, CheckCheck, Users, X, Check, Circle, ChevronDown,
  Archive, ArchiveRestore, Inbox, CalendarRange, CheckSquare,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { fmtDMY } from "@/lib/format";

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function MessagesPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [view, setView] = useState("list"); // list | compose | thread
  const [showArchived, setShowArchived] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null); // thread pending delete confirm (from list)
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmBulkDel, setConfirmBulkDel] = useState(false);
  const [hiddenThreads, setHiddenThreads] = useState(new Set()); // optimistic delete (undo window)
  const threadTimers = useRef({});

  useEffect(() => {
    const timers = threadTimers.current;
    return () => { Object.values(timers).forEach((tmr) => clearTimeout(tmr)); };
  }, []);

  const { data: threads = [], isLoading } = useQuery({
    queryKey: ["msg-threads", showArchived],
    queryFn: async () => (await api.get(`/messages/threads?archived=${showArchived}`)).data,
    refetchInterval: view === "list" ? 30000 : false,
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["msg-threads"] });
    qc.invalidateQueries({ queryKey: ["messages-unread-count"] });
  };

  const bulk = useMutation({
    mutationFn: ({ action }) => api.post("/messages/threads/bulk", { ids: [...selected], action }),
    onSuccess: (r, { action }) => {
      invalidateAll(); setSelected(new Set()); setSelectMode(false);
      if (action === "delete") {
        const d = r.data;
        toast.success(t("{{all}} deleted for everyone, {{me}} removed from your view", { all: d.deleted_for_everyone, me: d.removed_from_view }));
      } else toast.success(action === "archive" ? t("{{count}} conversation(s) archived", { count: r.data.count }) : t("{{count}} conversation(s) restored", { count: r.data.count }));
    },
    onError: (e) => toast.error(e.response?.data?.detail || t("Could not update conversations")),
  });
  const toggleSel = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const archive = useMutation({
    mutationFn: ({ tid, un }) => api.post(`/messages/threads/${tid}/${un ? "unarchive" : "archive"}`),
    onSuccess: (_, { un }) => { invalidateAll(); toast.success(un ? t("Conversation restored") : t("Conversation archived")); },
    onError: (e) => toast.error(e.response?.data?.detail || t("Could not archive conversation")),
  });

  // Delete a whole conversation with a confirm already done: hide it, show an Undo toast,
  // and only hit the server after the grace window (fully reversible with no server call).
  const deleteThreadWithUndo = (tid, scope = "all") => {
    setActiveId(null);
    setView("list");
    setHiddenThreads((prev) => new Set(prev).add(tid));
    const timer = setTimeout(async () => {
      delete threadTimers.current[tid];
      try {
        await api.delete(`/messages/threads/${tid}`);
        invalidateAll();
      } catch (e) {
        setHiddenThreads((prev) => { const n = new Set(prev); n.delete(tid); return n; });
        toast.error(e.response?.data?.detail || t("Could not delete conversation"));
      }
    }, 5000);
    threadTimers.current[tid] = timer;
    toast(scope === "all" ? t("Conversation deleted for everyone") : t("Conversation removed from your view"), {
      duration: 5000,
      action: {
        label: t("Undo"),
        onClick: () => {
          const tmr = threadTimers.current[tid];
          if (tmr) { clearTimeout(tmr); delete threadTimers.current[tid]; }
          setHiddenThreads((prev) => { const n = new Set(prev); n.delete(tid); return n; });
        },
      },
    });
  };

  if (view === "compose") {
    return <Composer onDone={(id) => { invalidateAll(); if (id) { setActiveId(id); setView("thread"); } else setView("list"); }} onCancel={() => setView("list")} />;
  }
  if (view === "thread" && activeId) {
    return <ThreadView id={activeId} onBack={() => { invalidateAll(); setView("list"); }} onReplied={invalidateAll} onDeleteThread={deleteThreadWithUndo} />;
  }

  const visibleThreads = threads.filter((th) => !hiddenThreads.has(th.id));
  const titleOf = (th) => {
    const names = (th.participants || []).map((p) => p.name).filter(Boolean);
    return th.recipient_type === "everyone" ? t("Everyone")
      : th.recipient_type === "cleaners" ? t("All cleaners")
      : th.recipient_type === "managers" ? t("All managers")
      : th.recipient_type === "system" ? t("KlenaPro reminders")
      : names.join(", ") || t("Conversation");
  };

  return (
    <div className="space-y-3" data-testid="messages-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-indigo-500" />
          <span className="font-display text-lg font-extrabold">{showArchived ? t("Archived messages") : t("Messages")}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant={selectMode ? "secondary" : "ghost"} onClick={() => { setSelectMode((v) => !v); setSelected(new Set()); }} data-testid="select-mode-btn" className="gap-1.5">
            <CheckSquare className="h-4 w-4" /> {selectMode ? t("Done") : t("Select")}
          </Button>
          <Button size="sm" variant={showArchived ? "secondary" : "outline"} onClick={() => { setShowArchived((v) => !v); setSelected(new Set()); }} data-testid="archived-toggle-btn" className="gap-1.5">
            {showArchived ? <Inbox className="h-4 w-4" /> : <Archive className="h-4 w-4" />} {showArchived ? t("Inbox") : t("Archived")}
          </Button>
          {!showArchived && (
            <Button size="sm" onClick={() => setView("compose")} data-testid="new-message-btn" className="gap-1.5">
              <Plus className="h-4 w-4" /> {t("New message")}
            </Button>
          )}
        </div>
      </div>
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {showArchived
          ? <><Archive className="h-3.5 w-3.5" /> {t("Grouped by pay period. Conversations quiet for 30 days are archived automatically; a new reply moves one back to your inbox.")}</>
          : <><Languages className="h-3.5 w-3.5" /> {t("Messages are automatically translated to each person's language.")}</>}
      </p>

      {selectMode && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border bg-secondary/40 px-3 py-2 text-xs" data-testid="bulk-bar">
          <button type="button" className="underline-offset-2 hover:underline" data-testid="bulk-select-all"
            onClick={() => setSelected(selected.size === visibleThreads.length ? new Set() : new Set(visibleThreads.map((x) => x.id)))}>
            {selected.size === visibleThreads.length && visibleThreads.length > 0 ? t("Clear selection") : t("Select all")}
          </button>
          <span className="font-semibold" data-testid="bulk-count">{t("{{count}} selected", { count: selected.size })}</span>
          <span className="ml-auto flex gap-1">
            <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" disabled={!selected.size || bulk.isPending} onClick={() => bulk.mutate({ action: showArchived ? "unarchive" : "archive" })} data-testid="bulk-archive">
              {showArchived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />} {showArchived ? t("Move back to inbox") : t("Archive")}
            </Button>
            <Button size="sm" variant="outline" className="h-7 gap-1 text-xs text-destructive" disabled={!selected.size || bulk.isPending} onClick={() => setConfirmBulkDel(true)} data-testid="bulk-delete">
              <Trash2 className="h-3.5 w-3.5" /> {t("Delete")}
            </Button>
          </span>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : visibleThreads.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-md border border-dashed py-14 text-center" data-testid="messages-empty">
          {showArchived ? <Archive className="h-7 w-7 text-muted-foreground" /> : <MessageSquare className="h-7 w-7 text-muted-foreground" />}
          <p className="text-sm text-muted-foreground">{showArchived ? t("Nothing archived yet.") : t("No messages yet. Start a conversation.")}</p>
        </div>
      ) : (
        <div className="divide-y divide-border rounded-md border">
          {visibleThreads.map((th, i) => {
            const prev = visibleThreads[i - 1];
            const newPeriod = showArchived && th.period_start && (!prev || prev.period_start !== th.period_start);
            return (
              <div key={th.id}>
                {newPeriod && (
                  <div className="flex items-center gap-2 bg-secondary/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground" data-testid={`period-sep-${th.period_start}`}>
                    <CalendarRange className="h-3.5 w-3.5" /> {t("Pay period")} {fmtDMY(th.period_start)} – {fmtDMY(th.period_end)}
                  </div>
                )}
                <div className={`group flex w-full items-start gap-3 px-3 py-3 hover:bg-secondary/50 ${selected.has(th.id) ? "bg-indigo-50" : ""}`} data-testid={`thread-row-${th.id}`}>
                  {selectMode && (
                    <Checkbox checked={selected.has(th.id)} onCheckedChange={() => toggleSel(th.id)} className="mt-2" data-testid={`thread-select-${th.id}`} />
                  )}
                  <button
                    onClick={() => { setActiveId(th.id); setView("thread"); }}
                    data-testid={`thread-item-${th.id}`}
                    className="flex min-w-0 flex-1 items-start gap-3 text-left"
                  >
                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-600">
                      {["everyone", "cleaners", "managers"].includes(th.recipient_type) ? <Megaphone className="h-4 w-4" /> : <MessageSquare className="h-4 w-4" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate font-semibold">{titleOf(th)}</span>
                        <span className="shrink-0 text-[11px] text-muted-foreground">{fmtWhen(th.last_at)}</span>
                      </div>
                      <div className="truncate text-sm text-muted-foreground">
                        {th.last_sender ? <span className="font-medium">{th.last_sender}: </span> : null}{th.last_text}
                      </div>
                    </div>
                  </button>
                  {th.unread > 0 && (
                    <span className="mt-1 inline-flex min-w-5 items-center justify-center rounded-full bg-indigo-500 px-1.5 py-0.5 text-[11px] font-bold text-white" data-testid={`thread-unread-${th.id}`}>
                      {th.unread}
                    </span>
                  )}
                  <div className="mt-0.5 flex shrink-0 items-center gap-0.5">
                    <button type="button" onClick={() => archive.mutate({ tid: th.id, un: showArchived })} disabled={archive.isPending}
                      title={showArchived ? t("Move back to inbox") : t("Archive")} data-testid={`thread-archive-${th.id}`}
                      className="rounded p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground">
                      {showArchived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                    </button>
                    {th.can_delete && (
                      <button type="button" onClick={() => setConfirmDel(th)} title={th.delete_scope === "all" ? t("Delete for everyone") : t("Remove from my view")} data-testid={`thread-delete-${th.id}`}
                        className="rounded p-1.5 text-muted-foreground hover:bg-secondary hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={confirmBulkDel} onOpenChange={setConfirmBulkDel}>
        <AlertDialogContent data-testid="bulk-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("Delete {{count}} conversation(s)?", { count: selected.size })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("Conversations you started are deleted for everyone; the rest are only removed from your view. Deleted conversations are kept in the super admin's vault and can be restored.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel">{t("Cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirmBulkDel(false); bulk.mutate({ action: "delete" }); }} data-testid="bulk-delete-confirm-btn" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent data-testid="list-thread-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDel?.delete_scope === "all" ? t("Delete this conversation for everyone?") : t("Remove this conversation from your view?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDel?.delete_scope === "all"
                ? t("You started this conversation, so it will disappear for everyone in it. You'll get a few seconds to undo.")
                : t("Only you will stop seeing it — the sender and other people keep their copy. You'll get a few seconds to undo.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="list-thread-delete-cancel">{t("Cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { const th = confirmDel; setConfirmDel(null); deleteThreadWithUndo(th.id, th.delete_scope); }}
              data-testid="list-thread-delete-confirm-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {confirmDel?.delete_scope === "all" ? t("Delete for everyone") : t("Remove for me")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Composer({ onDone, onCancel }) {
  const { t } = useTranslation();
  const [type, setType] = useState("everyone");
  const [userId, setUserId] = useState("");
  const [userIds, setUserIds] = useState([]);
  const [text, setText] = useState("");

  const { data: recipients = [] } = useQuery({
    queryKey: ["msg-recipients"],
    queryFn: async () => (await api.get("/messages/recipients")).data,
  });

  const toggleUser = (id) =>
    setUserIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const send = useMutation({
    mutationFn: async () => (await api.post("/messages/threads", {
      recipients: {
        type,
        user_id: type === "user" ? userId : null,
        user_ids: type === "users" ? userIds : null,
      },
      text: text.trim(),
    })).data,
    onSuccess: (d) => { toast.success(t("Message sent")); onDone(d?.id); },
    onError: (e) => toast.error(e.response?.data?.detail || t("Could not send message")),
  });

  const canSend = text.trim() && (type !== "user" || userId) && (type !== "users" || userIds.length > 0);

  return (
    <div className="space-y-4" data-testid="message-composer">
      <button onClick={onCancel} data-testid="composer-back" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> {t("Back")}
      </button>
      <div className="space-y-1.5">
        <label className="label-caps text-muted-foreground">{t("To")}</label>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="composer-to-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="everyone">{t("Everyone")}</SelectItem>
            <SelectItem value="cleaners">{t("All cleaners")}</SelectItem>
            <SelectItem value="managers">{t("All managers")}</SelectItem>
            <SelectItem value="user">{t("A specific person")}</SelectItem>
            <SelectItem value="users">{t("Pick specific people")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {type === "user" && (
        <div className="space-y-1.5">
          <label className="label-caps text-muted-foreground">{t("Choose a person")}</label>
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger data-testid="composer-person-select"><SelectValue placeholder={t("Select…")} /></SelectTrigger>
            <SelectContent className="max-h-72">
              {recipients.map((r) => (
                <SelectItem key={r.id} value={r.id} data-testid={`composer-person-${r.id}`}>
                  {r.name} · {r.role === "manager" ? t("Manager") : t("Cleaner")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {type === "users" && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="label-caps text-muted-foreground">{t("Pick people")}</label>
            <span className="text-xs text-muted-foreground" data-testid="composer-selected-count">
              {t("{{count}} selected", { count: userIds.length })}
            </span>
          </div>
          <div className="max-h-60 space-y-0.5 overflow-y-auto rounded-md border p-1.5" data-testid="composer-people-list">
            {recipients.map((r) => (
              <label
                key={r.id}
                htmlFor={`pick-${r.id}`}
                className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-secondary/60"
                data-testid={`composer-pick-${r.id}`}
              >
                <Checkbox
                  id={`pick-${r.id}`}
                  checked={userIds.includes(r.id)}
                  onCheckedChange={() => toggleUser(r.id)}
                  data-testid={`composer-pick-checkbox-${r.id}`}
                />
                <span className="flex-1 text-sm">{r.name}</span>
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${r.role === "manager" ? "bg-indigo-100 text-indigo-700" : "bg-emerald-100 text-emerald-700"}`}>
                  {r.role === "manager" ? t("Manager") : t("Cleaner")}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
      {type === "users" && userIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5 rounded-md border border-dashed border-border p-2" data-testid="composer-chips">
          {userIds.map((uid) => {
            const r = recipients.find((x) => x.id === uid);
            if (!r) return null;
            return (
              <span
                key={uid}
                className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700"
                data-testid={`composer-chip-${uid}`}
              >
                {r.name}
                <button
                  type="button"
                  onClick={() => toggleUser(uid)}
                  className="rounded-full p-0.5 hover:bg-indigo-200"
                  data-testid={`composer-chip-remove-${uid}`}
                  title={t("Remove")}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}
      <div className="space-y-1.5">
        <label className="label-caps text-muted-foreground">{t("Message")}</label>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("Type your message…")}
          rows={4}
          data-testid="composer-text"
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>{t("Cancel")}</Button>
        <Button onClick={() => send.mutate()} disabled={!canSend || send.isPending} data-testid="composer-send" className="gap-2">
          {send.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {t("Send")}
        </Button>
      </div>
    </div>
  );
}

function ThreadView({ id, onBack, onReplied, onDeleteThread }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [showOrig, setShowOrig] = useState({});
  const [showSeen, setShowSeen] = useState({}); // per-message read-receipt breakdown
  const [confirmDelId, setConfirmDelId] = useState(null); // message pending confirm
  const [confirmDelThread, setConfirmDelThread] = useState(false); // conversation delete confirm
  const [hiddenIds, setHiddenIds] = useState(new Set()); // optimistically hidden (undo window)
  const timersRef = useRef({});

  useEffect(() => {
    const timers = timersRef.current;
    return () => { Object.values(timers).forEach((tmr) => clearTimeout(tmr)); };
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ["msg-thread", id],
    queryFn: async () => (await api.get(`/messages/threads/${id}`)).data,
    refetchInterval: 20000,
  });

  useEffect(() => {
    if (data) qc.invalidateQueries({ queryKey: ["messages-unread-count"] });
  }, [data, qc]);

  const reply = useMutation({
    mutationFn: async () => (await api.post(`/messages/threads/${id}/reply`, { text: text.trim() })).data,
    onSuccess: () => { setText(""); qc.invalidateQueries({ queryKey: ["msg-thread", id] }); onReplied?.(); },
    onError: (e) => toast.error(e.response?.data?.detail || t("Could not send message")),
  });

  const delMsg = useMutation({
    mutationFn: async (mid) => (await api.delete(`/messages/${mid}`)).data,
    onSuccess: (d) => {
      onReplied?.();
      if (d?.thread_deleted) { onBack(); }
      else { qc.invalidateQueries({ queryKey: ["msg-thread", id] }); }
    },
    onError: (e, mid) => {
      // restore the message if the server delete failed
      setHiddenIds((prev) => { const n = new Set(prev); n.delete(mid); return n; });
      toast.error(e.response?.data?.detail || t("Could not delete message"));
    },
  });

  // Confirm → hide immediately, show an Undo toast, and only hit the server after the
  // undo window elapses (so an accidental delete is fully reversible with no server call).
  const stageDelete = (mid) => {
    setConfirmDelId(null);
    setHiddenIds((prev) => new Set(prev).add(mid));
    const timer = setTimeout(() => {
      delete timersRef.current[mid];
      delMsg.mutate(mid);
    }, 5000);
    timersRef.current[mid] = timer;
    toast(t("Message deleted"), {
      duration: 5000,
      action: {
        label: t("Undo"),
        onClick: () => {
          const tmr = timersRef.current[mid];
          if (tmr) { clearTimeout(tmr); delete timersRef.current[mid]; }
          setHiddenIds((prev) => { const n = new Set(prev); n.delete(mid); return n; });
        },
      },
    });
  };

  const names = (data?.participants || []).map((p) => p.name).filter(Boolean);
  const title = data?.recipient_type === "everyone" ? t("Everyone")
    : data?.recipient_type === "cleaners" ? t("All cleaners")
    : data?.recipient_type === "managers" ? t("All managers")
    : data?.recipient_type === "system" ? t("KlenaPro reminders")
    : names.join(", ") || t("Conversation");

  return (
    <div className="flex h-[60vh] flex-col" data-testid="thread-view">
      <div className="mb-2 flex items-center gap-2 border-b border-border pb-2">
        <button onClick={onBack} data-testid="thread-back" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <span className="truncate font-display font-bold">{title}</span>
        {data?.can_delete && (
          <button
            onClick={() => setConfirmDelThread(true)}
            data-testid="thread-delete"
            className="ml-auto flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive"
            title={data.delete_scope === "all" ? t("Delete for everyone") : t("Remove from my view")}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1" data-testid="thread-messages">
        {isLoading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          (data?.messages || []).filter((m) => !hiddenIds.has(m.id)).map((m) => (
            <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 ${m.mine ? "bg-indigo-500 text-white" : "bg-secondary"}`} data-testid={`msg-${m.id}`}>
                {!m.mine && <div className="mb-0.5 text-[11px] font-bold opacity-80">{m.sender_name}</div>}
                <div className="whitespace-pre-wrap text-sm">{m.text}</div>
                {m.translated && (
                  <button
                    onClick={() => setShowOrig((s) => ({ ...s, [m.id]: !s[m.id] }))}
                    data-testid={`msg-toggle-orig-${m.id}`}
                    className={`mt-1 flex items-center gap-1 text-[10px] ${m.mine ? "text-indigo-100" : "text-muted-foreground"}`}
                  >
                    <Languages className="h-3 w-3" /> {showOrig[m.id] ? t("Hide original") : t("Translated · show original")}
                  </button>
                )}
                {m.translated && showOrig[m.id] && (
                  <div className={`mt-1 whitespace-pre-wrap border-t pt-1 text-xs ${m.mine ? "border-indigo-300 text-indigo-100" : "border-border text-muted-foreground"}`}>
                    {m.original_text}
                  </div>
                )}
                <div className={`mt-1 flex items-center gap-2 text-[10px] ${m.mine ? "text-indigo-100" : "text-muted-foreground"}`}>
                  <span>{fmtWhen(m.created_at)}</span>
                  {m.can_delete && (
                    <button
                      onClick={() => setConfirmDelId(m)}
                      data-testid={`msg-delete-${m.id}`}
                      title={m.delete_scope === "all" ? t("Delete for everyone") : t("Remove from my view")}
                      className={`inline-flex items-center transition-opacity hover:opacity-100 ${m.mine ? "text-indigo-100 opacity-70" : "text-muted-foreground opacity-60 hover:text-destructive"}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
                {m.mine && m.total_recipients > 0 && (
                  <div className="mt-0.5" data-testid={`msg-seen-${m.id}`}>
                    <button
                      type="button"
                      onClick={m.total_recipients > 1 ? () => setShowSeen((s) => ({ ...s, [m.id]: !s[m.id] })) : undefined}
                      className={`flex items-center gap-1 text-[10px] text-indigo-100 ${m.total_recipients > 1 ? "hover:opacity-100" : "cursor-default"}`}
                      data-testid={`msg-seen-toggle-${m.id}`}
                    >
                      <CheckCheck className={`h-3 w-3 ${m.seen_count > 0 ? "opacity-100" : "opacity-50"}`} />
                      {m.seen_count === 0
                        ? t("Sent")
                        : m.seen_count >= m.total_recipients
                          ? t("Seen by everyone")
                          : t("Seen by {{count}}/{{total}}", { count: m.seen_count, total: m.total_recipients })}
                      {m.total_recipients > 1 && (
                        <ChevronDown className={`h-3 w-3 transition-transform ${showSeen[m.id] ? "rotate-180" : ""}`} />
                      )}
                    </button>
                    {m.total_recipients > 1 && showSeen[m.id] && (
                      <div className="mt-1 space-y-0.5 rounded-md bg-indigo-400/30 p-1.5" data-testid={`msg-seen-detail-${m.id}`}>
                        {(m.seen_by || []).map((n, i) => (
                          <div key={`s-${i}`} className="flex items-center gap-1 text-[10px] text-white">
                            <Check className="h-3 w-3 shrink-0" /> <span className="truncate">{n}</span>
                          </div>
                        ))}
                        {(m.unseen_by || []).map((n, i) => (
                          <div key={`u-${i}`} className="flex items-center gap-1 text-[10px] text-indigo-100/70">
                            <Circle className="h-2.5 w-2.5 shrink-0" /> <span className="truncate">{n}</span>
                            <span className="opacity-70">· {t("not seen")}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mt-2 flex items-end gap-2 border-t border-border pt-2">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("Type a reply…")}
          rows={2}
          data-testid="reply-text"
          className="min-h-[44px] resize-none"
        />
        <Button onClick={() => reply.mutate()} disabled={!text.trim() || reply.isPending} data-testid="reply-send" className="gap-1.5">
          {reply.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>

      <AlertDialog open={!!confirmDelId} onOpenChange={(o) => !o && setConfirmDelId(null)}>
        <AlertDialogContent data-testid="msg-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDelId?.delete_scope === "all" ? t("Delete this message for everyone?") : t("Remove this message from your view?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelId?.delete_scope === "all"
                ? t("You sent this message, so it will disappear for everyone. You'll get a few seconds to undo.")
                : t("Only you will stop seeing it — the sender keeps their copy. You'll get a few seconds to undo.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="msg-delete-cancel">{t("Cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => stageDelete(confirmDelId.id)}
              data-testid="msg-delete-confirm-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {confirmDelId?.delete_scope === "all" ? t("Delete for everyone") : t("Remove for me")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDelThread} onOpenChange={setConfirmDelThread}>
        <AlertDialogContent data-testid="thread-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{data?.delete_scope === "all" ? t("Delete this conversation for everyone?") : t("Remove this conversation from your view?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {data?.delete_scope === "all"
                ? t("You started this conversation, so it will disappear for everyone in it. You'll get a few seconds to undo.")
                : t("Only you will stop seeing it — the sender and other people keep their copy. You'll get a few seconds to undo.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="thread-delete-cancel">{t("Cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setConfirmDelThread(false); onDeleteThread?.(id, data?.delete_scope); }}
              data-testid="thread-delete-confirm-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {data?.delete_scope === "all" ? t("Delete for everyone") : t("Remove for me")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
